import React from 'react';
import s from './s.module.less';
import PaymentTable from 'components/ManageSubscription/components/PaymentTable';
import { Button, Popover, Select, Spin, Table } from 'antd';
import type { TableProps } from 'antd';
import useTotalPayoutsReceived from 'hooks/useTotalPayoutsReceived';
import useEarningAmount from 'hooks/useEarningAmount';
import useProviderPayoutHistoryList from 'hooks/useProviderPayoutHistoryList';
import KlarityPayoutDetailsModal from './components/KlarityPayoutDetailsModal';
import TaxDocumentsModal from './components/TaxDocumentsModal';
import { checkBillAccountCreated } from 'api/appointmentSetting';
import PayoutAccountSetup from 'pages/PPAAdvanceSettingPage/components/PayoutAccountSetup';
import ProviderCell from 'components/ProviderCell';

const { Option } = Select;

type TPayoutSummaryRow = {
    payoutId: number;
    payoutDate: string;
    amount: number;
    providerName?: string;
    providerAvatar?: string;
    providerCredential?: string;
};

interface Props {
    isPractice?: boolean;
}

const BillingPayoutsTable = ({ isPractice = false }: Props) => {
    const [selectedPeriod, setSelectedPeriod] = React.useState(1);
    const [modalSelectedPeriod, setModalSelectedPeriod] = React.useState(1);
    const [current, setCurrent] = React.useState(1);
    const pageSize = 10;
    const [detailsModalOpen, setDetailsModalOpen] = React.useState(false);
    const [taxDocumentsModalOpen, setTaxDocumentsModalOpen] = React.useState(false);
    const [sortBy, setSortBy] = React.useState<'PAYOUT_DATE' | 'AMOUNT'>('PAYOUT_DATE');
    const [orderType, setOrderType] = React.useState<'ASC' | 'DESC'>('DESC');
    const [totalPayoutsReceived, totalPayoutsReceivedLoading] = useTotalPayoutsReceived(selectedPeriod, isPractice);
    const [modalTotalPayoutsReceived, modalTotalPayoutsReceivedLoading] = useEarningAmount(
        modalSelectedPeriod,
        isPractice,
    );
    const [payouts, payoutsTotal, payoutsLoading] = useProviderPayoutHistoryList({
        timeRangeMonths: selectedPeriod,
        current,
        size: pageSize,
        sortBy,
        orderType,
        isPractice,
    });

    const [billAccountStatus, setBillAccountStatus] = React.useState<'NOT_CREATED' | 'SUCCESS' | 'FAILED' | undefined>(
        undefined,
    );
    const [billAccountErrorMessage, setBillAccountErrorMessage] = React.useState<string | undefined>(undefined);
    const [billAccountStatusLoading, setBillAccountStatusLoading] = React.useState(true);

    const fetchBillAccountStatus = React.useCallback(async () => {
        try {
            setBillAccountStatusLoading(true);
            const res = await checkBillAccountCreated();
            if (!res?.error) {
                const data = res.data?.data;
                setBillAccountStatus(data?.status);
                setBillAccountErrorMessage(data?.errorMessage || undefined);
            }
        } finally {
            setBillAccountStatusLoading(false);
        }
    }, []);

    React.useEffect(() => {
        fetchBillAccountStatus();
    }, [fetchBillAccountStatus]);

    const payoutSummaryColumns = React.useMemo(
        () => [
            {
                title: 'DATE',
                dataIndex: 'payoutDate',
                key: 'payoutDate',
                className: s.tableColDate,
                sorter: true,
                sortDirections: ['ascend', 'descend', 'ascend'] as const,
            },
            {
                title: 'PAYOUT',
                dataIndex: 'amount',
                key: 'amount',
                className: s.tableColPayout,
                sorter: true,
                sortDirections: ['ascend', 'descend', 'ascend'] as const,
                render: (amount: number) => {
                    const val = amount ?? 0;
                    return `$${Number(val).toFixed(2)}`;
                },
            },
            // Practice View attributes each payout to a provider.
            ...(isPractice
                ? [
                      {
                          title: 'PROVIDER',
                          key: 'providerName',
                          render: (_: unknown, record: TPayoutSummaryRow) => (
                              <ProviderCell
                                  name={record.providerName}
                                  avatar={record.providerAvatar}
                                  credential={record.providerCredential}
                              />
                          ),
                      },
                  ]
                : []),
        ],
        [isPractice],
    );

    const onTableChange: TableProps<TPayoutSummaryRow>['onChange'] = (_pagination, _filters, sorter) => {
        if (sorter && !Array.isArray(sorter) && sorter.field && sorter.order) {
            const newSortBy = sorter.field === 'payoutDate' ? 'PAYOUT_DATE' : 'AMOUNT';
            const newOrderType = sorter.order === 'ascend' ? 'ASC' : 'DESC';
            setSortBy(newSortBy);
            setOrderType(newOrderType);
            setCurrent(1);
        }
    };

    React.useEffect(() => {
        setCurrent(1);
    }, [selectedPeriod]);

    const payoutSummaryData: TPayoutSummaryRow[] =
        payouts?.map((r) => ({
            payoutId: r.payoutId,
            payoutDate: r.payoutDate,
            amount: r.amount,
            providerName: r.providerName || '',
            providerAvatar: r.providerAvatar,
            providerCredential: r.providerCredential,
        })) || [];

    if (billAccountStatusLoading) {
        return <Spin spinning />;
    }

    if (billAccountStatus !== 'SUCCESS') {
        return (
            <PayoutAccountSetup
                standalone
                onNext={fetchBillAccountStatus}
                onBack={() => {}}
                errorMessage={billAccountStatus === 'FAILED' ? billAccountErrorMessage : undefined}
            />
        );
    }

    return (
        <div className={s.wrap}>
            <div className={s.topActions}>
                <Button
                    className={s.actionBtn}
                    onClick={() => {
                        setDetailsModalOpen(true);
                    }}
                >
                    View Klarity payout details
                </Button>
            </div>

            <div className={s.overviewSectionItem}>
                <div className={s.h}>
                    <div className={s.hLeft}>
                        <span>Total payouts received from Klarity</span>
                    </div>
                    <div className={s.hRight}>
                        <Popover
                            trigger="hover"
                            content={
                                <div className={s.pop}>
                                    Total payouts you've received from Klarity, including policy-related fees (no-show,
                                    late cancellation, rescheduling), co-pays, cash-pay charges, and text visit payouts.
                                </div>
                            }
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                style={{ cursor: 'pointer', flexShrink: 0 }}
                            >
                                <path
                                    d="M13 16H12V12H11M12 8H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                                    stroke="#4B5563"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </Popover>
                        <Select
                            value={selectedPeriod}
                            onChange={(value) => setSelectedPeriod(value)}
                            className={s.select}
                            dropdownStyle={{ minWidth: '160px' }}
                        >
                            <Option value={1}>Last 1 month</Option>
                            <Option value={2}>Last 2 months</Option>
                            <Option value={3}>Last 3 months</Option>
                            <Option value={4}>Last 4 months</Option>
                            <Option value={5}>Last 5 months</Option>
                            <Option value={6}>Last 6 months</Option>
                        </Select>
                    </div>
                </div>
                <div className={s.priceWrap}>
                    <Spin spinning={totalPayoutsReceivedLoading} wrapperClassName={s.price}>
                        {totalPayoutsReceived == null ? 'N/A' : `$${totalPayoutsReceived}`}
                    </Spin>
                    <span className={s.priceDesc} />
                </div>
            </div>

            <div className={s.tableWrap}>
                <Table<TPayoutSummaryRow>
                    columns={payoutSummaryColumns as any}
                    dataSource={payoutSummaryData}
                    loading={payoutsLoading}
                    onChange={onTableChange}
                    pagination={{
                        current,
                        pageSize,
                        total: payoutsTotal,
                        onChange: (page) => setCurrent(page),
                        showTotal: (total) => `${total} records`,
                    }}
                    rowKey="payoutId"
                    className={s.summaryTable}
                    locale={{
                        emptyText: (
                            <div className={s.emptyText}>
                                <div>No payouts yet.</div>
                                <div>
                                    {'If you have any questions, contact '}
                                    <a href="mailto:providersupport@helloklarity.com">
                                        providersupport@helloklarity.com
                                    </a>
                                </div>
                            </div>
                        ),
                    }}
                />
            </div>

            <KlarityPayoutDetailsModal
                open={detailsModalOpen}
                onClose={() => setDetailsModalOpen(false)}
                selectedPeriod={modalSelectedPeriod}
                onChangeSelectedPeriod={(val) => setModalSelectedPeriod(val)}
                totalPayoutsReceived={modalTotalPayoutsReceived}
                totalPayoutsReceivedLoading={modalTotalPayoutsReceivedLoading}
            >
                <PaymentTable timeRangeMonths={modalSelectedPeriod} isGroupPractice={isPractice} />
            </KlarityPayoutDetailsModal>

            <TaxDocumentsModal open={taxDocumentsModalOpen} onClose={() => setTaxDocumentsModalOpen(false)} />
        </div>
    );
};

export default BillingPayoutsTable;
